/**
 * Dynamic legal page. Reads the slug from the URL (/legal/:slug) and pulls
 * its content from the CMS under keys: legal.{slug}.{title|eyebrow|last_updated|body}.
 * Unknown slug → redirect to /.
 */
import { Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/neptune/Navbar";
import Footer from "../components/neptune/Footer";
import PageHeader from "../components/neptune/PageHeader";
import { useContent } from "../context/ContentContext";
import { LEGAL_PAGES } from "../lib/legal";

export default function LegalPage() {
  const { slug } = useParams();
  const { t } = useContent();

  const meta = LEGAL_PAGES.find((p) => p.slug === slug);
  if (!meta) return <Navigate to="/" replace />;

  const title = t(`legal.${slug}.title`, meta.title);
  const eyebrow = t(`legal.${slug}.eyebrow`, meta.shortLabel);
  const lastUpdated = t(`legal.${slug}.last_updated`, "February 2026");
  const body = t(`legal.${slug}.body`, "Content coming soon.");

  return (
    <main
      data-testid={`legal-page-${slug}`}
      className="relative min-h-screen bg-[#0a0a0a] text-white font-body overflow-x-hidden"
    >
      <Navbar />

      <PageHeader
        eyebrow={eyebrow}
        title={title}
        sub={
          lastUpdated ? (
            <span className="text-[11px] uppercase tracking-[0.22em] text-zinc-600">
              Last updated · {lastUpdated}
            </span>
          ) : null
        }
      />

      <section className="relative py-12 sm:py-20 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <motion.article
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`legal-body-${slug}`}
            className="text-[15.5px] sm:text-[16.5px] text-zinc-300 leading-[1.85] whitespace-pre-line"
          >
            {body}
          </motion.article>
        </div>
      </section>

      <Footer />
    </main>
  );
}
